<template lang="pug">
    div
        Steps(:current="batchStep" style="margin-bottom: 20px")
            Step(v-for="(stepInfo, index) in batchStepData" :title="stepInfo.title" :content="stepInfo.content" :key="index")

        // 選擇要編輯的物件
        component(
          v-show="batchStep == 0"
          :is="batchSelector"
          :selector="batchSelectorWith"
          v-model="batchSelectedData"
          v-bind="{givenSearchFilters: givenBatchSearchFilters}"
          :esIndex="esIndex")

        // 選擇要編輯的欄位
        CheckboxGroup(v-show="batchStep == 1" v-model="selectedBatchEditFields")
            List
                ListItem(v-for="option in batchEditFormItems" :key="option.key")
                    Checkbox(:label="option.key") {{option.title}}

        // 表單
        Form(v-show="batchStep == 2" :model="batchFormData" ref="form" label-position="left" :label-width="250")
            FormItem
                div(slot="label")
                    span {{ $t("rcmtable.batchEditMode") }}&nbsp;
                    Tooltip(placement="top" max-width="300" transfer)
                        template(slot="content")
                            p {{ $t("rcmtable.batchEditModeHint") }}
                        Icon(type="ios-help-circle-outline" size="16" style="cursor: pointer")
                RadioGroup(v-model="batchEditMode")
                    Radio(label="replace") {{ $t("rcmtable.batchEditModeReplace") }}
                    Radio(label="adjust") {{ $t("rcmtable.batchEditModeAdjust") }}
            FormItem(v-if="batchEditMode == 'adjust'" :label="$t('rcmtable.batchEditAdjustAction')")
                RadioGroup(v-model="batchAdjustAction")
                    Radio(label="add") {{ $t("rcmtable.batchEditAdjustAdd") }}
                    Radio(label="remove") {{ $t("rcmtable.batchEditAdjustRemove") }}
            FormItem(v-for="item in fillOutBatchEditItems" :key="item.key" :prop="item.key" :rules="item.validate")
                div(slot="label")
                    h3 {{item.title}}
                    .formItemDescription(v-html="item.description")
                component(
                      :is="item.formRender"
                      v-model="batchFormData[item.key]"
                      :formData="batchFormData"
                      :formItem="item"
                      :editingItems="batchFormData"
                      :selectedItems="batchSelectedData.selectedItems")

        // 使用者確認
        Row(v-show="batchStep == 3")
            Col(span="11")
                Card
                    p(slot="title") Selected {{ batchSelectedData.selectedItems.length }} Items
                    p(v-for="item in batchSelectedData.selectedItems") {{ item }}
            Col(span="11" offset="2")
                Card
                    p(slot="title") Changed Fields
                    p {{ $t("rcmtable.batchEditMode") }}: {{ batchEditModeText }}
                    p(v-if="batchEditMode == 'adjust'") {{ $t("rcmtable.batchEditAdjustAction") }}: {{ batchAdjustActionText }}
                    p(v-for="(value, name) in effectiveBatchFormData") {{ name }}: {{ value }}

        // 功能鍵
        .footer
            Button(v-show="batchStep !== 0" style="width:45%" @click="changeBatchFormStep('prev')") Prev
            Button(v-if="batchStep !== 3" style="width:45%; float: right" @click="changeBatchFormStep('next')" :disabled="batchStepData[batchStep].isNextBtnDisabled()" ) Next
            Button(v-else style="width:45%; float: right" type="primary" @click="submitForm") {{ $t("rcmtable.submitText") }}

</template>
<script>
import FormInput from "./rcmTableFormItems/Input";
import FormInputNumber from "./rcmTableFormItems/InputNumber";
import FormSelect from "./rcmTableFormItems/Select/index.js";
import FormSwitch from "./rcmTableFormItems/Switch";
import FormCheckboxGroup from "./rcmTableFormItems/CheckboxGroup";
import FormRadioGroup from "./rcmTableFormItems/RadioGroup";
import FormUpload from "./rcmTableFormItems/Upload";
import FormTransfer from "./rcmTableFormItems/Transfer";
import TwoListDraggable from "./rcmTableFormItems/TwoListDraggable";
import OneListDraggable from "./rcmTableFormItems/OneListDraggable";
import TableList from "./rcmTableFormItems/TableList";
import RawHTML from "./rcmTableFormItems/RawHTML";
import TextOnly from "./rcmTableFormItems/TextOnly";

import FQDNSelector from "@/components/FQDNSelector.vue";

import { batchStepDataCompletion } from "@/libs/rcmTableFieldsFunction";
import { _batchUpdateData } from "@/api/esdata";

const BATCH_ADJUSTABLE_FIELDS = [
    // 陣列欄位
    "tags",
    "scheme",
    "httpPort",
    "httpsPort",
    "origins",
    "usedWafRules",
    "usedOriginPolicies",
    "customVariables",
    "proxySetHeader",
    "rewriteUrlRules",
    "cacheUrlRules",
    // 換行字串欄位（一行一筆）
    "description",
    "websocketPath",
    "cusCachepath",
    "cusCachefile",
    "cusNoncachepath",
    "cusNonachefile",
    "forceCusCachePath",
    "forceCusCacheFile",
    "cusFollowCachePath",
    "cusFollowCacheFile",
];

export default {
    components: {
        FormInput,
        FormInputNumber,
        FormSelect,
        FormSwitch,
        FormCheckboxGroup,
        FormRadioGroup,
        FormUpload,
        FormTransfer,
        TwoListDraggable,
        OneListDraggable,
        TableList,
        RawHTML,
        TextOnly,
        FQDNSelector,
    },
    props: {
        esIndex: { type: String, default: "" },
        allFormItems: { type: Array, default: () => [] },
        formData: { type: Object, default: () => {} },
        batchSelector: { type: String, default: "" },
        givenBatchSearchFilters: { type: Array, default: () => [] },
        batchSelectorWith: {},
        editingItems: {},
    },
    data() {
        return {
            // 批次表單內容表單
            batchFormData: {},
            batchEditMode: "replace",
            batchAdjustAction: "add",

            // 要批次修改的物件
            batchSelectedData: {
                selectedGroupName: "",
                selectedItems: [],
            },

            // 可編輯欄位列表選項
            batchEditFormItems: [],

            // 選中編輯欄位
            selectedBatchEditItems: [],
            selectedBatchEditFields: [],

            batchStep: 0,
            batchStepData: [
                {
                    title: "Select Items",
                    content: "Select the items to edit",
                    isNextBtnDisabled: () => !this.hasSelectedItems,
                    beforeNextStep: this.initBatchFormItems,
                },
                {
                    title: "Select Fields",
                    content: "Select the fields to edit",
                    isNextBtnDisabled: () => !this.hasSelectedEditFields,
                    beforeNextStep: this.initSelectedBatchFormItems,
                    validateBeforeNextStep: this.validateSelectedFields 
                },
                {
                    title: "Fill out",
                    content: "Fill out the form",
                    enter: this.initBatchFormData,
                    validateBeforeNextStep: this.validateForm,
                },
                { title: "Confirm", content: "Confirm the changes" },
            ],
        };
    },
    created() {},
    mounted() {
        // 補上預設值
        this.initBatchStepData();
    },
    watch: {},
    methods: {
        initBatchStepData() {
            this.batchStepData = this.batchStepData.map((data) => batchStepDataCompletion(data));
        },
        batchUpdateData(esIndex, formData, selectedItems, batchEditAction) {
            _batchUpdateData(esIndex, formData, selectedItems, batchEditAction)
                .then((result) => {
                    this.$Notice.success({
                        title: this.$t("global.success"),
                        desc: `${result.data.message}`,
                    });
                    this.$emit("batchEditSubmitComplete");
                })
                .catch((error) => {
                    let errorObject = JSON.parse(JSON.stringify(error));
                    this.$Notice.warning({
                        title: this.$t("global.error"),
                        desc: `${error.data.message}`,
                    });
                    this.$emit("batchEditSubmitComplete");
                });
        },
        async changeBatchFormStep(step) {
            if (step == "next") {
                // 執行驗證與 beforeNextStep 功能
                let passValidate = await this.batchStepData[this.batchStep].validateBeforeNextStep();

                if (passValidate) {
                    this.batchStepData[this.batchStep].beforeNextStep();
                    this.batchStep += 1;
                }
            } else {
                this.batchStep -= 1;
            }

            // 執行 enter 功能
            this.batchStepData[this.batchStep].enter();
        },
        redirectHttpsValidate() {
            const isRedirectHttpsSelected = this.selectedBatchEditFields.includes('redirecthttps')
            const isSchemeSelected = this.selectedBatchEditFields.includes('scheme')
            const inValidRedirectSelected = isRedirectHttpsSelected && !isSchemeSelected 

            if(inValidRedirectSelected) {
                this.$Modal.warning({
                    title: this.$t("global.warning"),
                    content: this.$t("rcmtable.batchEditRedirectValidateError"),
                })
            }
            return !inValidRedirectSelected
        },
        validateSelectedFields() {
            const redirecthttpsValid = this.redirectHttpsValidate()
            return redirecthttpsValid
        },
        async validateForm() {
            let valid = await this.$refs.form.validate();
            if (!valid) {
                this.$Message.error("表單有誤!");
                return false;
            }
            return true;
        },
        submitForm() {
            this.batchUpdateData(
                this.esIndex,
                this.effectiveBatchFormData,
                this.batchSelectedData.selectedItems,
                this.batchEditActionPayload
            );
        },
        isAdjustableBatchEditField(field) {
            return BATCH_ADJUSTABLE_FIELDS.includes(field.key);
        },
        initSelectedBatchFormItems() {
            this.selectedBatchEditItems = this.batchEditFormItems.filter((data) =>
                this.selectedBatchEditFields.includes(data.key)
            );
        },
        initBatchFormData() {
            var formData = {};

            this.selectedBatchEditItems.forEach((data) => {
                formData[data.key] = this.cloneDefaultVal(data.defaultVal);
            });
            formData.groupName = this.batchSelectedData.selectedGroupName;

            this.batchFormData = formData;
        },
        cloneDefaultVal(value) {
            if (Array.isArray(value)) {
                return value.slice();
            }
            if (value && typeof value == "object") {
                return Object.assign({}, value);
            }
            return value;
        },
        initBatchFormItems() {
            // 只做一次
            if (this.batchEditFormItems.length > 0) return;
            this.batchEditFormItems = this.allFormItems.filter((e) => {
                var formTypes = typeof e.formTypes == "function" ? e.formTypes() : e.formTypes;
                return formTypes.includes("multiEditForm") && !e.isFormSlot;
            });
        },
    },
    computed: {
        hasSelectedItems() {
            return this.batchSelectedData.selectedItems.length !== 0;
        },
        hasSelectedEditFields() {
            return this.selectedBatchEditFields.length !== 0;
        },
        fillOutBatchEditItems() {
            if (this.batchEditMode !== "adjust") return this.selectedBatchEditItems;
            return this.selectedBatchEditItems.filter((field) => this.isAdjustableBatchEditField(field));
        },
        effectiveBatchFormData() {
            if (this.batchEditMode !== "adjust") return this.batchFormData;

            const formData = {
                groupName: this.batchFormData.groupName,
            };
            this.fillOutBatchEditItems.forEach((field) => {
                if (Object.prototype.hasOwnProperty.call(this.batchFormData, field.key)) {
                    formData[field.key] = this.batchFormData[field.key];
                }
            });
            return formData;
        },
        batchEditActionPayload() {
            if (this.batchEditMode == "replace") return null;
            return {
                editMode: this.batchEditMode,
                action: this.batchAdjustAction,
            };
        },
        batchEditModeText() {
            const i18nKey = this.batchEditMode == "adjust" ? "batchEditModeAdjust" : "batchEditModeReplace";
            return this.$t(`rcmtable.${i18nKey}`);
        },
        batchAdjustActionText() {
            const i18nKey = this.batchAdjustAction == "remove" ? "batchEditAdjustRemove" : "batchEditAdjustAdd";
            return this.$t(`rcmtable.${i18nKey}`);
        },
    },
};
</script>
<style lang="less" scoped>
.footer {
    width: 100%;
    position: absolute;
    bottom: 0;
    left: 0;
    border-top: 1px solid #e8e8e8;
    padding: 15px 16px;
    text-align: left;
    background: #fff;
}
</style>



// WEBPACK FOOTER //
// src/components/BatchEditForm.vue